import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Container, Form, Button, Alert, FormCheck } from 'react-bootstrap';
import { FaCalculator, FaRedo } from 'react-icons/fa';
import './styles/hoc-calculator.css';
import { ResultsData, ToolData, FormationData } from './types/types';
import FormationsSection from './components/FormationsSection';
import ConstantsSection from './components/ConstantsSection';
import ToolsSection from './components/ToolsSection';
import ResultsSection from './components/ResultsSection';
import {
  calculateResultData,
  formatCurrency,
  formatNumber,
  validateFormInputs
} from './components/CalculatorUtils';

// Initial state values defined here for consistency
const initialFormations: FormationData = {
  capRockStart: '0',
  capRockEnd: '500',
  saltStart: '500',
  saltEnd: '800'
};

const initialTripTime = '15';
const initialRigCost = '3000';

const initialViperTool: ToolData = {
  hours: '100',
  cost: '41000.00',
  capROP: '3.0',
  saltROP: '13'
};

const initialRhoTool: ToolData = {
  hours: '50',
  cost: '24000.00',
  capROP: '3.0',
  saltROP: '13'
};

const initialConvTool: ToolData = {
  hours: '50',
  cost: '24000.00',
  capROP: '3.0',
  saltROP: '13'
};

const initialResults: ResultsData = {
  hoc: {
    capHours: 0,
    saltHours: 0,
    totalHours: 0,
    viperTools: 0,
    rhoTools: 0,
    totalCost: 0,
    toolCosts: 0,
    tripCosts: 0,
    drillHoursCost: 0
  },
  competitor: {
    capHours: 0,
    saltHours: 0,
    totalHours: 0,
    convTools: 0,
    totalCost: 0,
    toolCosts: 0,
    tripCosts: 0,
    drillHoursCost: 0
  },
  savings: 0,
  percentageSavings: 0
};

const HOOptimizationCalculator: React.FC = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Icon wrapper components to fix TypeScript issues
  const CalculatorIcon: React.FC<{ className?: string }> = ({ className }) => {
    const IconComponent = FaCalculator as any;
    return <IconComponent className={className} />;
  };

  const RedoIcon: React.FC<{ className?: string }> = ({ className }) => {
    const IconComponent = FaRedo as any;
    return <IconComponent className={className} />;
  };
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [formations, setFormations] =
    useState<FormationData>(initialFormations);

  const [tripTime, setTripTime] = useState<string>(initialTripTime);
  const [rigCost, setRigCost] = useState<string>(initialRigCost);
  const [viperTool, setViperTool] = useState<ToolData>(initialViperTool);
  const [rhoTool, setRhoTool] = useState<ToolData>(initialRhoTool);
  const [convTool, setConvTool] = useState<ToolData>(initialConvTool);
  const [results, setResults] = useState<ResultsData>(initialResults);

  const [copyChecked, setCopyChecked] = useState<boolean>(false); // New state for checkbox

  const handleFormationChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormations({
      ...formations,
      [name]: value
    });
  };

  const handleToolChange = (
    toolType: 'viper' | 'rho' | 'conv',
    field: keyof ToolData,
    value: string
  ): void => {
    let processedValue = value;

    if (field === 'cost') {
      processedValue = value.replace(/[^0-9.]/g, '');
    }

    switch (toolType) {
      case 'viper':
        setViperTool({ ...viperTool, [field]: processedValue });
        break;
      case 'rho':
        setRhoTool({ ...rhoTool, [field]: processedValue });
        break;
      case 'conv':
        setConvTool({ ...convTool, [field]: processedValue });
        if (copyChecked) {
          setRhoTool({ ...rhoTool, [field]: processedValue });
        }
        break;
    }
  };

  const handleCopyCheck = (e: ChangeEvent<HTMLInputElement>): void => {
    setCopyChecked(e.target.checked);
    if (e.target.checked) {
      setRhoTool({
        hours: convTool.hours,
        cost: convTool.cost,
        capROP: convTool.capROP,
        saltROP: convTool.saltROP
      });
    }
  };

  const calculateResults = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    setValidated(true);
    setFormSubmitted(true);

    // Validate the form
    const errors = validateFormInputs(formations, tripTime, rigCost);
    setFormErrors(errors);

    if (errors.length > 0) {
      return;
    }

    const calculatedResults = calculateResultData(
      formations,
      viperTool,
      rhoTool,
      convTool,
      tripTime,
      rigCost
    );

    setResults(calculatedResults);

    setTimeout(() => {
      const resultsElement = document.getElementById('results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const resetForm = (): void => {
    setFormations(initialFormations);
    setTripTime(initialTripTime);
    setRigCost(initialRigCost);
    setViperTool(initialViperTool);
    setRhoTool(initialRhoTool);
    setConvTool(initialConvTool);
    setResults(initialResults);
    setValidated(false);
    setFormErrors([]);
    setFormSubmitted(false);
    setCopyChecked(false);
  };

  return (
    <Container className="calculator-container">
      <div className="calculator-header">
        <h1 className="calculator-header-title">HOC Optimization Calculator</h1>
      </div>

      <Form noValidate validated={validated} onSubmit={calculateResults}>
        {formSubmitted && formErrors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading>Please correct the following errors:</Alert.Heading>
            <ul>
              {formErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Formations Section */}
        <FormationsSection
          formations={formations}
          handleFormationChange={handleFormationChange}
        />

        {/* Constants Section */}
        <ConstantsSection
          tripTime={tripTime}
          rigCost={rigCost}
          formatCurrency={formatCurrency}
          setTripTime={setTripTime}
          setRigCost={setRigCost}
        />

        {/* Tools Section */}
        <ToolsSection
          viperTool={viperTool}
          rhoTool={rhoTool}
          convTool={convTool}
          handleToolChange={handleToolChange}
          formatCurrency={formatCurrency}
        />

        <div className="d-flex justify-content-center mb-5">
          <Button type="submit" className="calculate-button me-3">
            <CalculatorIcon className="me-2" /> Calculate Results
          </Button>
          <Button
            variant="secondary"
            onClick={resetForm}
            className="reset-button me-3"
          >
            <RedoIcon className="me-2" /> Reset
          </Button>
          <FormCheck
            type="checkbox"
            label="Copy RHO/Conv"
            checked={copyChecked}
            onChange={handleCopyCheck}
          />
        </div>
      </Form>

      {/* Results Section - only displayed when calculation is complete and valid */}
      {formSubmitted && formErrors.length === 0 && (
        <ResultsSection
          results={results}
          formatNumber={formatNumber}
          formatCurrency={formatCurrency}
        />
      )}
    </Container>
  );
};

export default HOOptimizationCalculator;
